import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RotateCcw, Loader2, X, Check, Ban } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { inr } from "@/lib/format";
import AdminLayout from "./AdminLayout";

const TYPES = ["all", "experience", "flight", "hotel", "bus", "cab"];
const STATUS_STYLE = {
  confirmed: "bg-secondary text-primary", pending: "bg-secondary text-muted-foreground",
  completed: "bg-primary text-primary-foreground", cancelled: "bg-destructive/10 text-destructive",
};
const PAY_STYLE = { paid: "text-primary", pending: "text-muted-foreground", refunded: "text-destructive" };

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [type, setType] = useState("all");
  const [loading, setLoading] = useState(true);
  const [refundTarget, setRefundTarget] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    const q = type === "all" ? "" : `?type=${type}`;
    api.get(`/admin/bookings${q}`).then((r) => setBookings(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, [type]);

  const changeStatus = async (b, status) => {
    try { await api.patch(`/admin/bookings/${b.id}/status`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doRefund = async () => {
    setBusy(true);
    try { await api.post(`/admin/bookings/${refundTarget.id}/refund`, { reason }); toast.success("Booking refunded"); setRefundTarget(null); setReason(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <AdminLayout title="Bookings">
      <div className="flex flex-wrap gap-2 mb-6" data-testid="booking-type-filters">
        {TYPES.map((t) => (
          <button key={t} onClick={() => setType(t)} data-testid={`tfilter-${t}`}
            className={`rounded-md px-4 py-1.5 text-sm font-medium border capitalize transition-colors ${type === t ? "bg-primary text-primary-foreground border-primary" : "border-primary/15 text-foreground/70 hover:bg-secondary"}`}>{t}</button>
        ))}
      </div>
      <div className="rounded-md border border-primary/10 bg-card overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-10 rounded bg-secondary/60 animate-pulse" />)}</div>
        ) : bookings.length === 0 ? (
          <p className="p-10 text-center text-muted-foreground">No bookings in this view.</p>
        ) : (
          <table className="w-full text-sm min-w-[860px]" data-testid="admin-bookings-table">
            <thead><tr className="text-left text-muted-foreground border-b border-primary/10">
              <th className="px-5 py-3 font-medium">Customer</th><th className="px-5 py-3 font-medium">Item</th>
              <th className="px-5 py-3 font-medium">Type</th><th className="px-5 py-3 font-medium">Amount</th>
              <th className="px-5 py-3 font-medium">Status</th><th className="px-5 py-3 font-medium">Payment</th>
              <th className="px-5 py-3 font-medium text-right">Actions</th>
            </tr></thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} className="border-b border-primary/5 last:border-0" data-testid={`admin-booking-row-${b.id}`}>
                  <td className="px-5 py-3"><p className="text-foreground font-medium">{b.customer_name}</p><p className="text-xs text-muted-foreground">{b.customer_email}</p></td>
                  <td className="px-5 py-3 text-muted-foreground max-w-[220px]">{b.title}</td>
                  <td className="px-5 py-3 text-muted-foreground capitalize">{b.type}</td>
                  <td className="px-5 py-3 text-foreground font-medium">{inr(b.amount)}</td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold uppercase px-2.5 py-1 rounded-full ${STATUS_STYLE[b.status]}`}>{b.status}</span></td>
                  <td className="px-5 py-3"><span className={`text-xs font-semibold uppercase ${PAY_STYLE[b.payment_status] || ""}`}>{b.payment_status}</span></td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-2">
                      {b.status === "confirmed" && <button onClick={() => changeStatus(b, "completed")} title="Complete" className="grid place-items-center w-8 h-8 rounded-md border border-primary/15 hover:bg-secondary" data-testid={`complete-${b.id}`}><Check size={15} /></button>}
                      {["pending", "confirmed"].includes(b.status) && <button onClick={() => changeStatus(b, "cancelled")} title="Cancel" className="grid place-items-center w-8 h-8 rounded-md border border-primary/15 hover:bg-secondary" data-testid={`cancel-${b.id}`}><Ban size={15} /></button>}
                      {b.payment_status === "paid" && <button onClick={() => setRefundTarget(b)} className="inline-flex items-center gap-1.5 rounded-md bg-destructive/10 text-destructive px-3 h-8 text-xs font-semibold hover:bg-destructive/20" data-testid={`refund-${b.id}`}><RotateCcw size={14} /> Refund</button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {refundTarget && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-primary/40 backdrop-blur-sm p-4" data-testid="refund-modal">
          <div className="w-full max-w-md rounded-xl bg-card border border-primary/10 p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-lg text-foreground">Refund booking</h3><button onClick={() => setRefundTarget(null)}><X size={18} /></button></div>
            <p className="text-sm text-muted-foreground">Refund <b className="text-foreground">{inr(refundTarget.amount)}</b> to <b className="text-foreground">{refundTarget.customer_name}</b>. This cancels the booking.</p>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="Reason (optional)" data-testid="refund-reason"
              className="w-full mt-4 rounded-lg border border-input bg-transparent px-3 py-2 text-sm focus:ring-2 focus:ring-accent focus:outline-none" />
            <div className="flex justify-end gap-3 mt-5">
              <button onClick={() => setRefundTarget(null)} className="rounded-md px-4 py-2 text-sm font-medium border border-primary/15 hover:bg-secondary">Cancel</button>
              <button onClick={doRefund} disabled={busy} data-testid="confirm-refund" className="inline-flex items-center gap-2 rounded-md bg-destructive text-destructive-foreground px-4 py-2 text-sm font-semibold disabled:opacity-60">
                {busy ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />} Confirm refund
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
